/*
 * Stream 1 ad offers: a free gift from a cart total (one step or two), or a discount
 * from N pieces of one collection (two steps).
 *
 * Each offer is an entry of the `offer` metaobject, printed by
 * snippets/nom-offers-json.liquid. The ad links to /discount/<CODE>; Shopify keeps the
 * code on the cart. After every cart change this script keeps exactly one gift of the
 * right step in the cart and swaps the Shopify code between the steps (a code carries
 * one step only: IG-LADDER150 -> IG-LADDER300, IG-2FOR20 -> IG-3FOR25). The codes make
 * the gift free and give the discount; they do not combine with other codes.
 *
 * It also marks the cart with `_offer` (Liquid reads it for the top bar line on every
 * next page) and shows a one-time popup and the progress in the cart.
 * Codes are named <CHANNEL>-<ENDING> (IG-GIFT150, TT-LADDER300); the offer is found by
 * the ending.
 */
(function (root) {
  function normalizeCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function matchOffer(codes, offers) {
    if (!Array.isArray(codes) || !Array.isArray(offers)) return null;
    for (var i = 0; i < codes.length; i += 1) {
      var code = normalizeCode(codes[i]);
      var dash = code.lastIndexOf('-');
      if (dash <= 0) continue;
      var channel = code.slice(0, dash);
      var ending = code.slice(dash + 1);
      for (var j = 0; j < offers.length; j += 1) {
        var offer = offers[j];
        if (normalizeCode(offer.suffix1) === ending) return { offer: offer, channel: channel, code: code, tier: 1 };
        if (offer.suffix2 && normalizeCode(offer.suffix2) === ending) return { offer: offer, channel: channel, code: code, tier: 2 };
      }
    }
    return null;
  }

  function codeFor(channel, offer, tier) {
    return channel + '-' + normalizeCode(tier === 2 && offer.suffix2 ? offer.suffix2 : offer.suffix1);
  }

  // Shopify's /discount/<CODE> link stores the code in this cookie too; it outlives a
  // code that an app or an in-app browser drops from the cart.
  function codeFromCookie(cookie) {
    var match = /(?:^|;\s*)discount_code=([^;]*)/.exec(String(cookie || ''));
    return match ? normalizeCode(decodeURIComponent(match[1])) : '';
  }

  function resolveOffer(candidates, offers) {
    var codes = (candidates || []).filter(function (code) { return normalizeCode(code) !== ''; });
    return matchOffer(codes, offers);
  }

  function thresholdCents(euros, rate) {
    if (euros === null || euros === undefined) return null;
    return Math.round(Number(euros) * 100 * (Number(rate) || 1));
  }

  function giftIds(offer) {
    var ids = [];
    if (offer.gift1) ids.push(offer.gift1.id);
    if (offer.gift2) ids.push(offer.gift2.id);
    return ids;
  }

  // Lines the Monk app added (its own gift offers, still installed) carry `_mr`; the
  // total counts only what the customer put in the bag.
  function eligibleSubtotal(items, ids) {
    var total = 0;
    (items || []).forEach(function (line) {
      if ((ids || []).indexOf(line.variant_id) !== -1) return;
      if (line.properties && line.properties._mr) return;
      total += line.original_line_price;
    });
    return total;
  }

  function eligiblePieces(items, productIds) {
    var count = 0;
    (items || []).forEach(function (line) {
      if (line.properties && line.properties._mr) return;
      if ((productIds || []).indexOf(line.product_id) !== -1) count += line.quantity;
    });
    return count;
  }

  // What the steps are measured in: cents of the eligible total, or pieces. `allGiftIds`
  // holds the gifts of every offer, so a gift left from an earlier link never counts.
  function measureFor(items, offer, allGiftIds) {
    if (offer.type === 'pieces') return eligiblePieces(items, offer.productIds);
    return eligibleSubtotal(items, (allGiftIds || []).concat(giftIds(offer)));
  }

  function stepsFor(offer, rate) {
    if (offer.type === 'pieces') {
      return [Number(offer.threshold1), offer.suffix2 && offer.threshold2 ? Number(offer.threshold2) : null];
    }
    return [thresholdCents(offer.threshold1, rate), offer.gift2 ? thresholdCents(offer.threshold2, rate) : null];
  }

  function targetTier(measure, offer, rate) {
    var steps = stepsFor(offer, rate);
    if (steps[1] !== null && measure >= steps[1]) return 2;
    if (measure >= steps[0]) return 1;
    return 0;
  }

  function progressFor(measure, offer, rate) {
    var steps = stepsFor(offer, rate);
    var tier = targetTier(measure, offer, rate);
    if (tier === 0) return { tier: 0, remaining: steps[0] - measure, pct: Math.floor((measure * 100) / steps[0]) };
    if (tier === 1 && steps[1] !== null) return { tier: 1, remaining: steps[1] - measure, pct: Math.floor((measure * 100) / steps[1]) };
    return { tier: tier, remaining: null, pct: 100 };
  }

  // The cart shows one bar for the offer and free shipping. It runs to the offer's
  // last step; the steps before it — and free shipping, for money offers — are dots on
  // it. Free shipping past the last step would sit off the bar and is left out.
  function progressTrack(measure, offer, rate, shippingCents) {
    var steps = stepsFor(offer, rate);
    var max = steps[1] !== null ? steps[1] : steps[0];
    var pctOf = function (value) { return Math.min(100, Math.floor((value * 100) / max)); };
    var ticks = [];
    var step = steps[1] !== null ? { kind: 'step', pct: pctOf(steps[0]), reached: measure >= steps[0] } : null;
    if (offer.type !== 'pieces' && shippingCents !== null && shippingCents !== undefined && shippingCents < max) {
      var shipping = { kind: 'shipping', pct: pctOf(shippingCents), reached: measure >= shippingCents };
      // Two dots almost on top of each other read as a glitch; the step's dot stays.
      if (!step || Math.abs(step.pct - shipping.pct) >= 5) ticks.push(shipping);
    }
    if (step) ticks.push(step);
    return { pct: pctOf(measure), ticks: ticks };
  }

  // `updates` is the body for /cart/update.js: line keys to remove
  // or fix, and the gift's variant id to add. `code` is the code to put on the cart,
  // null when the right one is already there. Below the first step the cart keeps the
  // first code, so the offer stays known.
  function planSync(cart, offer, channel, rate, allGiftIds) {
    var items = cart.items || [];
    var ids = (allGiftIds || []).concat(giftIds(offer));
    var measure = measureFor(items, offer, ids);
    var tier = targetTier(measure, offer, rate);
    var updates = {};
    var wanted = null;

    if (offer.type !== 'pieces') {
      wanted = tier === 2 ? offer.gift2.id : tier === 1 ? offer.gift1.id : null;
      // A free gift from a Monk offer and our code (which combines with nothing)
      // cannot both apply: Shopify keeps the better one and charges full price for
      // the other gift. Ours steps aside rather than end up charged.
      var monkGiftFree = items.some(function (line) {
        return line.properties && line.properties._mr && line.original_line_price > 0 && line.final_line_price === 0;
      });
      if (monkGiftFree) wanted = null;
    }

    // Exactly one gift of the right step; gifts of other offers (an earlier link) go.
    var kept = false;
    items.forEach(function (line) {
      if (ids.indexOf(line.variant_id) === -1) return;
      if (line.variant_id !== wanted || kept) {
        updates[line.key] = 0;
        return;
      }
      kept = true;
      if (line.quantity !== 1) updates[line.key] = 1;
    });
    if (wanted !== null && !kept) updates[wanted] = 1;

    // A /discount/ link adds its code next to the ones already there; only the current
    // offer's code may stay, since the codes do not combine and Shopify could keep an
    // old one.
    var wantedCode = codeFor(channel, offer, tier === 2 ? 2 : 1);
    var current = (cart.discount_codes || []).map(function (c) { return normalizeCode(c.code); });
    return {
      tier: tier,
      measure: measure,
      updates: updates,
      code: current.length === 1 && current[0] === wantedCode ? null : wantedCode,
    };
  }

  var AJAX_CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var STOREFRONT_API = /\/api\/[^/]+\/graphql\.json/;
  var API_CART_MUTATION = /mutation[\s\S]*\bcart(Create|Lines\w+|DiscountCodesUpdate|BuyerIdentityUpdate|AttributesUpdate)\b/;

  // Apps change the cart through the Storefront API too (Monk does), so both kinds
  // of writes trigger a sync.
  function isCartWrite(url, body) {
    url = String(url || '');
    if (AJAX_CART_WRITE.test(url)) return true;
    return STOREFRONT_API.test(url) && typeof body === 'string' && API_CART_MUTATION.test(body);
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    // Node (tests): only the pure functions above are needed.
  } else {
    var OWN = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Nom-Offer': '1' };
    var nativeFetch = window.fetch.bind(window);
    var syncTimer = null;
    var syncing = false;
    // A cart write that lands while a sync is running is not dropped: one more sync runs right after.
    var syncAgain = false;
    var rechecked = false;
    // A write Shopify refuses (a sold-out gift, say) is not retried forever.
    var failures = 0;

    var readOffers = function () {
      var node = document.getElementById('nom-offers');
      if (!node) return [];
      try { return JSON.parse(node.textContent) || []; } catch (e) { return []; }
    };
    var rate = function () {
      return parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
    };
    var cartUrl = function (path) { return (window.routes && window.routes.cart_url || '/cart') + path; };
    // /cart.js now and then answers with an empty body right after a cart change;
    // one retry settles it.
    var getCart = function (retried) {
      return nativeFetch(cartUrl('.js'), { headers: OWN })
        .then(function (r) { return r.text(); })
        .then(function (text) { return JSON.parse(text); })
        .catch(function (error) {
          if (retried) throw error;
          return new Promise(function (resolve) { setTimeout(resolve, 400); }).then(function () { return getCart(true); });
        });
    };
    var post = function (url, body) {
      return nativeFetch(url, { method: 'POST', headers: OWN, body: JSON.stringify(body) }).then(function (r) {
        if (!r.ok) throw new Error(url + ' ' + r.status);
        return r.json();
      });
    };

    // `_offer` lets Liquid render the top bar line on every next page; `_offer_code`
    // keeps the code when an app or an in-app browser drops it from the cart.
    var attributesFor = function (cart, match, code) {
      var attributes = cart.attributes || {};
      var handle = match ? match.offer.handle : '';
      code = match ? code : '';
      if ((attributes._offer || '') === handle && (attributes._offer_code || '') === code) return null;
      return { _offer: handle, _offer_code: code };
    };

    // The cart page is Dawn's <cart-items>: re-render the same sections it re-renders
    // after a quantity change, so a gift the script added shows up without a reload.
    var cartItems = function () { return document.querySelector('cart-items'); };
    var cartSections = function (items) {
      var ids = [];
      items.getSectionsToRender().forEach(function (section) {
        if (ids.indexOf(section.section) === -1) ids.push(section.section);
      });
      return ids;
    };
    var refreshCartPage = function (state) {
      var items = cartItems();
      if (!items || !state.sections) return;
      try {
        items.classList.toggle('is-empty', state.item_count === 0);
        items.getSectionsToRender().forEach(function (section) {
          var html = state.sections[section.section];
          var holder = document.getElementById(section.id || section.ids);
          if (!html || !holder) return;
          (holder.querySelector(section.selector) || holder).innerHTML = items.getSectionInnerHTML(html, section.selector);
        });
        items.updateFreeShippingBar(state);
      } catch (e) {
        window.location.reload();
      }
    };
    var refreshCount = function (count) {
      var notification = document.querySelector('cart-notification');
      if (notification && typeof notification.updateCartCount === 'function') notification.updateCartCount(count);
    };

    // While the cart changes (the theme, an app or this script), the cart page dims its
    // lines and runs a loader along the bar, so the customer sees something is going on.
    var busyWrites = 0;
    var busySync = false;
    var busyTimer = null;
    var updateBusy = function () {
      if (!cartItems()) return;
      var on = busyWrites > 0 || busySync;
      document.body.classList.toggle('nom-cart-busy', on);
      clearTimeout(busyTimer);
      // Never leave the cart locked if a request goes quiet.
      if (on) busyTimer = setTimeout(function () { busyWrites = 0; busySync = false; updateBusy(); }, 8000);
    };

    var write = function (body) {
      var items = cartItems();
      busySync = true;
      updateBusy();
      if (items) {
        body.sections = cartSections(items);
        body.sections_url = window.location.pathname;
      }
      return post(cartUrl('/update.js'), body).then(function (state) {
        // A new code changes the prices on the cart page even when no line changed.
        if (body.updates || body.discount) refreshCartPage(state);
        if (body.updates) refreshCount(state.item_count);
        return state;
      });
    };

    var fillText = function (template, gift) {
      return String(template || '').split('{gift}').join(gift ? gift.title : '');
    };

    // On the landing page the server has not seen `_offer` yet and rendered the
    // slides. They are hidden, not removed: the slider's interval keeps a live
    // collection of .top-bar__item and would throw on an empty one.
    var renderBar = function (offer) {
      var bar = document.querySelector('[data-nom-offer-bar]');
      if (!bar) return;
      // The page may have been rendered for the offer of an earlier link (the cart's
      // `_offer` changes only now): the line is there, its text is not this offer's.
      var current = bar.querySelector('.nom-offer-bar__text');
      if (current) {
        current.textContent = fillText(offer.barText, offer.gift1);
        return;
      }
      var line = document.createElement('p');
      line.className = 'nom-offer-bar__line';
      var dot = document.createElement('span');
      dot.className = 'top-bar__dot nom-offer-bar__dot';
      dot.setAttribute('aria-hidden', 'true');
      var text = document.createElement('span');
      text.className = 'nom-offer-bar__text';
      text.textContent = fillText(offer.barText, offer.gift1);
      line.append(dot, text);
      bar.append(line);
      bar.classList.add('nom-offer-bar');
    };

    // Counted as seen only once the visitor closes it: the Monk app reloads the page
    // now and then, and a popup marked seen on open would just flash.
    var popupOpened = false;
    var openPopupOnce = function (offer) {
      var key = 'nomOfferPopup:' + offer.handle;
      if (popupOpened) return;
      try { if (sessionStorage.getItem(key)) return; } catch (e) { return; }
      var popup = document.querySelector('[data-nom-offer-popup]');
      if (!popup) return;
      popupOpened = true;
      popup.querySelector('.nom-offer-popup__heading').textContent = offer.popupHeading || '';
      popup.querySelector('.nom-offer-popup__text').textContent = offer.popupText || '';
      var image = popup.querySelector('.nom-offer-popup__image');
      if (offer.popupImage) {
        image.src = offer.popupImage;
        popup.querySelector('.nom-offer-popup__media').hidden = false;
        popup.classList.add('nom-offer-popup--media');
      }
      var close = function () {
        popup.classList.remove('is-open');
        document.documentElement.classList.remove('nom-offer-popup-open');
        popup.hidden = true;
        try { sessionStorage.setItem(key, '1'); } catch (e) { /* private mode: it just shows again */ }
        document.removeEventListener('keydown', onKey);
      };
      var onKey = function (event) { if (event.key === 'Escape') close(); };
      popup.querySelectorAll('[data-nom-offer-close]').forEach(function (el) {
        el.addEventListener('click', close);
      });
      document.addEventListener('keydown', onKey);
      popup.hidden = false;
      document.documentElement.classList.add('nom-offer-popup-open');
      // The card takes the focus rather than the button, which would open in its focus state.
      var card = popup.querySelector('.nom-offer-popup__card');
      card.setAttribute('tabindex', '-1');
      card.focus({ preventScroll: true });
      requestAnimationFrame(function () { popup.classList.add('is-open'); });
    };

    var money = function (cents) {
      var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency }).format(cents / 100);
    };

    // Same drawing style as the icons of the theme's free-shipping bar.
    var ICONS = {
      gift: '<svg class="free-shipping-bar__icon" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">'
        + '<path d="M3.75 9.25h12.5v7.5H3.75zM2.75 6.25h14.5v3H2.75zM10 6.25v10.5M10 6.25C9.1 4 6.6 3.2 6.1 4.6c-.45 1.3 1.9 1.65 3.9 1.65zm0 0c.9-2.25 3.4-3.05 3.9-1.65.45 1.3-1.9 1.65-3.9 1.65z" '
        + 'stroke="#1C1B1F" stroke-width="1.1" stroke-linejoin="round"/></svg>',
      pieces: '<svg class="free-shipping-bar__icon" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">'
        + '<path d="M10.6 3.25h5.9v5.9l-7.35 7.35-5.9-5.9z" stroke="#1C1B1F" stroke-width="1.1" stroke-linejoin="round"/>'
        + '<circle cx="13.4" cy="6.35" r="1.05" fill="#1C1B1F"/></svg>',
    };

    var shippingCents = function (box) {
      var raw = box.getAttribute('data-free-shipping-cents');
      return raw ? Math.round(Number(raw) * rate()) : null;
    };

    var renderProgress = function (offer, measure) {
      var box = document.getElementById('nom-offer-progress');
      if (!box) return;
      var p = progressFor(measure, offer, rate());
      var track = progressTrack(measure, offer, rate(), shippingCents(box));
      var template = p.remaining === null ? offer.progressDoneText : p.tier === 0 ? offer.progressText1 : offer.progressText2;
      var gift = p.remaining === null ? (p.tier === 2 ? offer.gift2 : offer.gift1) : p.tier === 0 ? offer.gift1 : offer.gift2;
      var remaining = p.remaining === null ? '' : offer.type === 'pieces' ? String(p.remaining) : money(p.remaining);

      box.querySelector('.nom-offer-progress__fill').style.width = track.pct + '%';
      var ticks = box.querySelector('.nom-offer-progress__ticks');
      ticks.innerHTML = '';
      track.ticks.forEach(function (tick) {
        var dot = document.createElement('span');
        dot.className = 'nom-offer-progress__tick' + (tick.reached ? ' is-reached' : '');
        dot.style.left = tick.pct + '%';
        ticks.append(dot);
      });

      var message = box.querySelector('.nom-offer-progress__message--offer');
      var text = document.createElement('span');
      text.textContent = fillText(template, gift).split('{remaining}').join(remaining);
      message.innerHTML = offer.type === 'pieces' ? ICONS.pieces : ICONS.gift;
      message.append(text);

      // Free shipping keeps the theme's own message (and its wording from the section
      // settings), moved under the offer line; the theme's separate bar is hidden.
      var shipping = box.querySelector('.nom-offer-progress__message--shipping');
      // The bar holds both of its messages and shows one (main-cart-items.liquid).
      var source = document.querySelector('#free-shipping-bar .free-shipping-bar__message:not([hidden])');
      shipping.innerHTML = source ? source.innerHTML : '';
      // The copied icon points at a <mask> by id, and that id now also lives inside the
      // hidden bar; the mask is a plain square anyway, so the copy goes without it.
      shipping.querySelectorAll('mask').forEach(function (el) { el.remove(); });
      shipping.querySelectorAll('[mask]').forEach(function (el) { el.removeAttribute('mask'); });
      shipping.hidden = !source;
      document.body.classList.add('nom-offer-in-cart');
      box.hidden = false;
    };

    document.addEventListener('nom-offer:change', function (event) {
      renderBar(event.detail.offer);
      openPopupOnce(event.detail.offer);
      renderProgress(event.detail.offer, event.detail.measure);
    });

    var scheduleSync = function (delay) {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(sync, delay || 150);
    };

    var allGiftIds = function () {
      var ids = [];
      readOffers().forEach(function (offer) { ids = ids.concat(giftIds(offer)); });
      return ids;
    };

    // Puts the right gift and code on the cart. Returns the cart as it is afterwards.
    var syncOffer = function (cart, match) {
      var plan = planSync(cart, match.offer, match.channel, rate(), allGiftIds());
      var code = codeFor(match.channel, match.offer, plan.tier === 2 ? 2 : 1);
      var body = {};
      if (Object.keys(plan.updates).length) body.updates = plan.updates;
      if (plan.code) body.discount = plan.code;
      var attributes = attributesFor(cart, match, code);
      if (attributes) body.attributes = attributes;
      if (!Object.keys(body).length || failures >= 3) return Promise.resolve(cart);
      return write(body).catch(function (error) {
        failures += 1;
        console.error('[nom-offer]', error);
        return getCart();
      });
    };

    var clearMark = function (cart) {
      var attributes = attributesFor(cart, null, '');
      return attributes ? post(cartUrl('/update.js'), { attributes: attributes }) : Promise.resolve();
    };

    var sync = function () {
      if (syncing) {
        syncAgain = true;
        return;
      }
      syncing = true;
      getCart()
        .then(function (cart) {
          var attributes = cart.attributes || {};
          var cartCodes = (cart.discount_codes || []).map(function (c) { return c.code; });
          var candidates = [codeFromCookie(document.cookie)].concat(cartCodes, [attributes._offer_code]);
          var match = resolveOffer(candidates, readOffers());
          // The Monk app rewrites the cart codes on load; look once more before giving up.
          if (!match && !rechecked) {
            rechecked = true;
            scheduleSync(2500);
            return null;
          }
          if (!match) return clearMark(cart);
          return syncOffer(cart, match).then(function (state) {
            document.dispatchEvent(new CustomEvent('nom-offer:change', {
              detail: { offer: match.offer, code: match.code, measure: measureFor(state.items, match.offer, allGiftIds()) },
            }));
          });
        })
        .catch(function (e) { console.error('[nom-offer]', e); })
        .finally(function () {
          syncing = false;
          if (syncAgain) {
            syncAgain = false;
            scheduleSync();
            return;
          }
          busySync = false;
          updateBusy();
        });
    };

    // Every add-to-cart path in the theme and the apps go through fetch; watching it
    // keeps the gift and the progress right without touching each one.
    window.fetch = function (input, init) {
      var promise = nativeFetch(input, init);
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var headers = (init && init.headers) || {};
      var own = headers['X-Nom-Offer'] === '1' || (typeof headers.get === 'function' && headers.get('X-Nom-Offer') === '1');
      if (!own && isCartWrite(url, init && init.body)) {
        busyWrites += 1;
        updateBusy();
        var settle = function () { busyWrites = Math.max(0, busyWrites - 1); };
        promise.then(function (response) {
          settle();
          if (response.ok) {
            busySync = true;
            scheduleSync();
          }
          updateBusy();
        }).catch(function () { settle(); updateBusy(); });
      }
      return promise;
    };

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { sync(); });
    else sync();
  }

  var api = {
    normalizeCode: normalizeCode,
    matchOffer: matchOffer,
    codeFor: codeFor,
    codeFromCookie: codeFromCookie,
    resolveOffer: resolveOffer,
    thresholdCents: thresholdCents,
    eligibleSubtotal: eligibleSubtotal,
    eligiblePieces: eligiblePieces,
    measureFor: measureFor,
    targetTier: targetTier,
    progressFor: progressFor,
    progressTrack: progressTrack,
    planSync: planSync,
    isCartWrite: isCartWrite,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
    return;
  }
  root.NomOffer = api;
})(typeof window !== 'undefined' ? window : this);
